import { FocusEvent, useMemo } from "react";
import { Theme, useTheme } from "@mui/material/styles";
import { SystemStyleObject } from "@mui/system";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import classNames from "classnames";
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TextFieldStyles } from "./TextField";
import moment, { Moment } from "moment";

export interface IYearFieldProps {
  name: string;
  value?: number;
  label?: string;
  onChange?: (value: number) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  className?: string;
  disabled?: boolean;
  meta?: Partial<FieldMetaProps<number>>;
  labelSx?: Record<string, string>;
  minYear?: number;
  maxYear?: number;
}

export const YearField = (props: IYearFieldProps): JSX.Element => {
  const { name, value, label, onChange, onBlur, className, disabled, meta, labelSx, minYear, maxYear } = props;
  const theme = useTheme();

  const displayValue = useMemo((): Moment | null => {
    return value ? moment().year(value).startOf("year") : null;
  }, [value]);

  const dirty = useMemo((): boolean => {
    return meta?.touched && value !== meta.initialValue;
  }, [value, meta]);

  const handleChange = (newValue: Moment | null): void => {
    if (!onChange) return;
    if (newValue && moment.isMoment(newValue) && newValue.isValid()) {
      onChange(newValue.year());
    }
  };

  const yearStyles = (theme: Theme): SystemStyleObject<Theme> => ({
    display: "flex",
    flexDirection: "column",
    margin: "5px",
    flexWrap: "wrap",
    width: "calc(100% - 10px)",
    "& .MuiFormLabel-root": {
      overflowWrap: "normal !important",
      whiteSpace: "normal !important",
      overflow: "initial !important",
      textOverflow: "initial !important",
      maxWidth: "unset !important",
    },
    "& .input_control": {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      "& .label": {
        display: "flex",
        justifyContent: "center",
        "& label": {
          position: "relative",
          width: "80px",
          textAlign: "right",
          transform: "none",
          color: theme.custom.forms.pristineColor,
          fontSize: 12,
          lineHeight: "12px",
          margin: "auto 10px auto auto",
          transition: theme.transitions.create(["color"]),
        },
      },
    },
    "&.dirty .input_control .label label": {
      color: `${theme.custom.forms.dirtyColor} !important`,
    },
    "&.error .input_control .label label": {
      color: theme.custom.forms.errorColor,
    },
    "&.disabled": {
      opacity: 0.5,
      pointerEvents: "none",
    },
  });

  return (
    <FormControl
      className={classNames(className, disabled && "disabled", dirty && "dirty", meta?.error && "error")}
      sx={(theme) => yearStyles(theme)}
      error={Boolean(meta?.error)}
    >
      <div className="input_control">
        {label && (
          <div className="label">
            <InputLabel htmlFor={`${name}_year_input`} sx={labelSx ?? {}}>
              {label}
            </InputLabel>
          </div>
        )}
        <DatePicker
          views={["year"]}
          openTo="year"
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          minDate={minYear ? moment().year(minYear).startOf("year") : undefined}
          maxDate={maxYear ? moment().year(maxYear).startOf("year") : undefined}
          slotProps={{
            textField: {
              id: `${name}_year_input`,
              className: "input-base",
              sx: TextFieldStyles(theme, null).dateTime,
              onBlur,
            },
            openPickerButton: {
              tabIndex: -1,
              sx: { padding: "2px !important" },
            },
          }}
        />
      </div>
      {meta?.error && typeof meta.error === "string" && <FieldError meta={meta} />}
    </FormControl>
  );
};
